import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Dialog, DialogActions, DialogTitle, DialogContent, Stack,
    CardHeader
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import PaymentIcon from "@mui/icons-material/Payment";
import { ListQuery } from "../../../Types/CommonTypes";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../../../components/LoadingSection";
import ResponsivePagination from "../../../components/ResponsivePagination";

const PaymentProviderConfigList: React.FC = () => {
    const { t } = useTranslation();
    const [items, setItems] = useState<PaymentProviderConfigViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{
                items: PaymentProviderConfigViewModel[];
                count: number;
            }>("/settingsApi/GetPaymentProviderConfigList", query);
            setItems(response.items);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/settings/paymentproviderconfig/edit", params: { id } });
    };

    const handleCreate = () => {
        setIsCreationModalOpen(true);
    };

    const handleCreateWithType = (paymentProviderType: number) => {
        setIsCreationModalOpen(false);
        navigate({
            path: "/settings/paymentproviderconfig/edit",
            params: { paymentProviderType },
        });
    };

    type CreationInputModalProps = {
        open: boolean;
        onClose: () => void;
        handleCreate: (paymentProviderType: number) => void;
    };

    const CreationModeModal = ({ open, onClose, handleCreate }: CreationInputModalProps) => {
        const paymentProviders = [
            { id: 50, name: "Adyen" },
            { id: 11, name: "Klarna" },
            { id: 70, name: "Swish" },
             { id: 10, name: "Klarna Legacy" },
             { id: 20, name: "DIBS" },
             { id: 30, name: "QLIRO" },
             { id: 40, name: "PayPal" },
             { id: 60, name: "Verifone" },
        ];


    return (
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: "80vw",
                        maxWidth: "none",
                        p: 3,
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center" }}>   {t("common.selectPaymentProvider")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} direction="column" alignItems="center" mt={2}>
                        {paymentProviders.map((p) => (
                            <Button
                                key={p.id}
                                variant="contained"
                                fullWidth
                                size="large"
                                sx={{ minWidth: 200 }}
                                onClick={() => handleCreate(p.id)}
                                color="primary"
                            >
                                {p.name}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>   {t("common.cancel")}</Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <CenteredListCard title={t("common.paymentProviderConfig")}>
          
                <CreationModeModal
                    open={isCreationModalOpen}
                    onClose={() => setIsCreationModalOpen(false)}
                    handleCreate={handleCreateWithType}
                />
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleCreate}
                    >
                        {t("common.create")}
                    </Button>
                </Box>

                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{ display: "flex", gap: 2, marginBottom: 2 }}
                >
                    <TextField
                        label={t("common.search")}
                        variant="outlined"
                        fullWidth
                        onChange={(event) => setFilter(event.target.value)}
                        value={filter}
                        disabled={loading}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading}
                    >
                        {t("common.search")}
                    </Button>
                </Box>

            <LoadingSection loading={loading}>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {items.map((item) => (
                        <Grid
                            item
                            xs={12}
                            sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
                            md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}
                            key={item.id}
                        >
                            <Card
                                onClick={() => handleSelect(item.id)}
                                sx={{
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow: 4,
                                        backgroundColor: "secondary.main",
                                    },
                                }}
                            >
                                <CardContent>
                                    <ListCardHeader
                                        icon={<PaymentIcon fontSize="small" color="action" />}
                                        title={item.id}
                                    />
                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.paymentProvider")}: {item.paymentProviderDesc}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {item.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {items.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
            </LoadingSection>

                <ResponsivePagination


                    page={query.page}


                    pageSize={query.pageSize}


                    totalCount={totalCount}


                    onPageChange={handlePageChange}


                />
      
        </CenteredListCard>
    );
};

export default PaymentProviderConfigList;
